import React, { useState, useEffect, useCallback } from "react";
import {
  Page,
  Layout,
  Card,
  DataTable,
  Badge,
  Spinner,
  Banner,
  EmptyState,
  Text,
  Box,
  Button,
  InlineGrid,
  BlockStack,
} from "@shopify/polaris";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

function Kpi({ label, value, sub }) {
  return (
    <Card>
      <Box padding="400">
        <BlockStack gap="100">
          <Text variant="bodySm" tone="subdued">{label}</Text>
          <Text variant="headingLg" as="p">{value}</Text>
          {sub && <Text variant="bodySm" tone="subdued">{sub}</Text>}
        </BlockStack>
      </Box>
    </Card>
  );
}

export default function AnalyticsPage() {
  const { apiFetch, shopDomain } = useShop();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/analytics/conversions${refresh ? "?refresh=1" : ""}`);
      setData(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load, shopDomain]);

  const k = data?.kpis;
  const sessions = data?.sessions || [];

  const fmtInput = (s) => {
    const parts = [];
    if (s.input?.height) parts.push(`${s.input.height} cm`);
    if (s.input?.weight) parts.push(`${s.input.weight} kg`);
    if (s.input?.age) parts.push(`${s.input.age} ${t("analytics.yearsShort")}`);
    if (s.input?.fitPreference && s.input.fitPreference !== "regular") parts.push(s.input.fitPreference);
    if (parts.length === 0 && s.bodyMeasurements?.chest) {
      return `${t("analytics.aiEstimate")}: ${s.bodyMeasurements.chest}/${s.bodyMeasurements.waist || "–"}`;
    }
    return parts.join(" · ") || "—";
  };

  const fmtSizes = (s) => {
    if (!s.recommendedSizes) return "—";
    return Object.entries(s.recommendedSizes)
      .map(([g, size]) => `${g}: ${size}`)
      .join(" · ");
  };

  const fmtOrder = (s) => {
    if (!s.order) return s.checkedAt ? "—" : <Badge tone="info">{t("analytics.notChecked")}</Badge>;
    const items = (s.order.lineItems || [])
      .map((li) => `${li.title}${li.variantTitle ? ` (${li.variantTitle})` : ""}${li.quantity > 1 ? ` ×${li.quantity}` : ""}`)
      .join(", ");
    return (
      <BlockStack gap="050">
        <Text variant="bodySm" fontWeight="semibold">
          {s.order.name} · {new Date(s.order.createdAt).toLocaleDateString("nl-NL")}
          {s.order.total ? ` · ${s.order.currency || ""} ${s.order.total}` : ""}
        </Text>
        {items && <Text variant="bodySm" tone="subdued">{items}</Text>}
      </BlockStack>
    );
  };

  const rows = sessions.map((s) => [
    new Date(s.createdAt).toLocaleDateString("nl-NL"),
    s.email || "—",
    s.productTitle || s.productId || "—",
    fmtInput(s),
    fmtSizes(s),
    fmtOrder(s),
    s.order ? (
      s.returned ? (
        <Badge tone="critical">
          {t("analytics.returnedYes")}{s.returnSource === "zyncport" ? " · Zyncport" : ""}
        </Badge>
      ) : (
        <Badge tone="success">{t("analytics.returnedNo")}</Badge>
      )
    ) : "—",
  ]);

  return (
    <Page
      title={t("analytics.title")}
      subtitle={t("analytics.subtitle")}
      primaryAction={{
        content: refreshing ? t("analytics.refreshing") : t("analytics.refresh"),
        onAction: () => load(true),
        loading: refreshing,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
          </Layout.Section>
        )}
        {data?.refresh?.remaining > 0 && (
          <Layout.Section>
            <Banner tone="info">
              {t("analytics.moreToCheck", { count: data.refresh.remaining })}
            </Banner>
          </Layout.Section>
        )}
        {k && (
          <Layout.Section>
            <InlineGrid columns={{ xs: 2, sm: 3, md: 5 }} gap="300">
              <Kpi label={t("analytics.kpiSessions")} value={k.totalSessions} />
              <Kpi label={t("analytics.kpiCustomers")} value={k.uniqueCustomers} />
              <Kpi
                label={t("analytics.kpiOrdered")}
                value={`${k.conversionRate}%`}
                sub={t("analytics.kpiOrderedSub", { count: k.customersOrdered })}
              />
              <Kpi
                label={t("analytics.kpiReturned")}
                value={`${k.returnRate}%`}
                sub={t("analytics.kpiReturnedSub", { count: k.customersReturned })}
              />
              <Kpi label={t("analytics.kpiPending")} value={k.pendingChecks} />
            </InlineGrid>
          </Layout.Section>
        )}
        <Layout.Section>
          <Card>
            {loading ? (
              <Box padding="400"><Spinner /></Box>
            ) : sessions.length === 0 ? (
              <EmptyState
                heading={t("analytics.noData")}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>{t("analytics.noDataText")}</p>
              </EmptyState>
            ) : (
              <DataTable
                columnContentTypes={["text", "text", "text", "text", "text", "text", "text"]}
                headings={[
                  t("analytics.colDate"),
                  t("analytics.colCustomer"),
                  t("analytics.colProduct"),
                  t("analytics.colInput"),
                  t("analytics.colAdvice"),
                  t("analytics.colOrder"),
                  t("analytics.colReturned"),
                ]}
                rows={rows}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
